
import { Component, Vue } from 'vue-property-decorator'
import { IS_PRODUCTION } from '@/common/variables'
import { isPhoneValid } from '@/util/isPhoneValid'
import { encryptPassword } from '@/util/encryption'

interface Form {
  userName: string
  password: string
  checkPassword: string
  phone: string
  code: string
}

interface VerifyCodeData {
  imgString: string
  requestId: string
}

enum Step {
  First = 'first',
  Second = 'second'
}

// eslint-disable-next-line no-magic-numbers
const TIME_COUNT = 60

@Component
export default class PersonDetail extends Vue {
  validateName = (rule: any, val: string | number | Date, callback: (arg0?: Error | undefined) => void) => {
    if (val === this.form.password) {
      callback(new Error('密码不能和账号名称相同'))
    } else {
      callback()
    }
  }

  validatePassword = (rule: any, val: string | number | Date, callback: (arg0?: Error | undefined) => void) => {
    if (val === this.form.userName) {
      callback(new Error('密码不能和账号名称相同'))
    } else {
      callback()
    }
  }

  validateCheckPassword = (rule: any, val: string | number | Date, callback: (arg0?: Error | undefined) => void) => {
    if (val !== this.form.password) {
      callback(new Error('两次输入密码不一致'))
    } else {
      callback()
    }
  }

  validatePhone = async (rule: any, val: string | number | Date, callback: (arg0?: Error | undefined) => void) => {
    if (!isPhoneValid(`${val}`)) {
      callback(new Error('手机号不合法，请重新输入'))
    } else {
      callback()
    }
  }

  $refs!: {
    form: HTMLFormElement
    extraForm: HTMLFormElement
  }

  form: Form = {
    userName: '',
    password: '',
    checkPassword: '',
    phone: '',
    code: '',
  }

  extraForm = {
    name: '',
    job: '',
    email: '',
    demand: '',
    industry: '',
    city: []
  }

  extraRules = {
    name: [
      { required: true, message: '公司不能为空', trigger: 'blur' },
    ],
    job: [
      { required: true, message: '职位不能为空', trigger: 'blur' },
    ],
    email: [
      { required: true, message: '邮箱不能为空', trigger: 'blur' },
      { type: 'email', message: '邮箱格式不正确，请重新输入', trigger: 'blur' },
    ],
  }

  verifyCodeData: VerifyCodeData = {
    imgString: '',
    requestId: '',
  }

  verify = ''

  isShowPassword = false

  isShowCheckPassword = false

  timer = 0

  count = 0

  checked = false

  cityOptions = []

  industryOptions = []

  isShowVerify = false

  rules = {
    userName: [
      { required: true, message: '账号名称不能为空', trigger: 'blur' },
      { validator: this.validateName, trigger: 'blur' },
    ],
    password: [
      { required: true, message: '请输入密码', trigger: 'blur' },
      { validator: this.validatePassword, trigger: 'blur' },
    ],
    checkPassword: [
      { required: true, message: '请再次输入密码', trigger: 'blur' },
      { validator: this.validateCheckPassword, trigger: 'blur' },
    ],
    phone: [
      { required: true, message: '手机号不能为空', trigger: 'blur' },
      { validator: this.validatePhone, trigger: 'blur' },
    ],
    code: [{ required: true, message: '验证码不能为空', trigger: 'blur' }],
  }

  get isCodeSent() {
    return this.isShowVerify ? (this.form.phone && this.verify) : this.form.phone
  }

  get submitDisabled() {
    return this.form.userName && this.form.password && this.form.checkPassword && this.form.phone && this.form.code && !this.checked
  }

  get registerDisabled() {
    return !this.extraForm.email || !this.extraForm.name || !this.extraForm.job
  }

  mounted() {
    this.getVerifyCode()
    this.getCityOptions()
    this.getIndustryOptions()
  }

  async getCityOptions() {
    const result = await (
        await fetch('https://opencdn.datastory.com.cn/ds-account/city.json')
      ).json()
    this.cityOptions = result.provinces
  }

  async getIndustryOptions() {
    this.industryOptions = await (
        await fetch('https://opencdn.datastory.com.cn/ds-account/industry.json')
      ).json()
  }

  async getVerifyCode() {
    this.verifyCodeData = await this.$ucProxy({
      url: '/api/v1/captchas/getPic',
      type: 'post',
    })
  }

  step: Step = Step.First
  get atFirstStep() {
    return this.step === Step.First
  }
  get atSecondStep() {
    return this.step === Step.Second
  }

  toNextStep() {
    this.$refs.form.validate(async (valid: boolean) => {
      if (valid) {
        this.step = Step.Second
      }
    })
  }

  toPrevStep() {
    this.step = Step.First
  }

  handleSubmit() {
    if (IS_PRODUCTION && window.collectEvent) {
      window.collectEvent('register_click', {
        register_click_url: window.location.href
      })
    }
    this.$refs.extraForm.validate(async (valid: boolean) => {
      const { redirect_url } = this.$route.query
      if (valid) {
        const isSmi = redirect_url && /(smi\.|smi-uat)/.test(redirect_url as string)
        const res = await this.$ucProxy({
          url: '/api/v1/account/register',
          type: 'POST',
          data: JSON.stringify({
            ...this.form,
            // 在 uc 的注册页面逻辑中，如果 redirect_url 是 smi 榜单（包括 dev 和正式），在提交注册的接口，将企业参数改为 企业名称+SMI＋手机号码后四位 https://jira.ds-int.cn/browse/BNR-18591
            name: isSmi ? `${this.extraForm.name}SMI${this.form.phone.slice(-4)}` : this.extraForm.name,
            attributes: [
              {
                name: 'job',
                value: this.extraForm.job
              },
              {
                name: 'demand',
                value: this.extraForm.demand
              },
            ],
            email: isSmi ? this.extraForm.email.split('@').join(`_smi${this.form.phone.slice(-4)}@`) : this.extraForm.email,
            industry: this.extraForm.industry,
            address: this.extraForm.city[1],
            type: 'INDIVIDUAL',
            // 区分短信模板
            applicationId: IS_PRODUCTION ? '219' : '445',
            source: isSmi ? 'SMI' : 'APPSTORE'
          }),
          contentType: 'application/json;charset=UTF-8',
        })
        if (IS_PRODUCTION && window.collectEvent) {
          window.collectEvent('config', {
            user_unique_id: res.userId
          })
          window.collectEvent('register_success', {
            register_success_url: redirect_url
          })
        }
        const password = await encryptPassword(this, this.form.password)
        await this.$ucProxy({
          url: '/user/login',
          type: 'POST',
          data: {
            username: res.email,
            password,
          },
        })
        this.$store.commit('RESET_SESSION')

        if (redirect_url && !redirect_url.includes('appstore.')) {
          location.replace(redirect_url as string)
        } else {
          this.$router.push({ name: 'RegisterSuccess', query: this.$route.query })
        }
      }
    })
  }

  changeNewPassword() {
    this.isShowPassword = !this.isShowPassword
  }

  changeCheckPassword() {
    this.isShowCheckPassword = !this.isShowCheckPassword
  }

  async sendCodePort() {
    try {
      await this.$ucProxy({
        url: '/api/v1/account/verification',
        type: 'POST',
        data: JSON.stringify({
          phone: this.form.phone,
          requestId: this.isShowVerify ? this.verifyCodeData.requestId : '',
          code: this.isShowVerify ?this.verify : '',
          type: 'PHONE',
          applicationId: process.env.NODE_ENV === 'development' || location.hostname.includes('.dev.') ? '445' : '219',
        }),
        contentType: 'application/json;charset=UTF-8',
      }, {
        showMeg: false
      })
      this.countTime()
      this.$dsMessage.success('验证码已发送')
    } catch (err) {
      const typedError = err as AxiosError
      this.$dsMessage.error(typedError.responseJSON.message)
      if (typedError.responseJSON.id === '43017') {
        this.isShowVerify = true
      }
    }
  }

  countTime() {
    if (!this.timer) {
      this.count = TIME_COUNT
      this.timer = window.setInterval(() => {
        if (this.count > 0 && this.count <= TIME_COUNT) {
          this.count--
        } else {
          clearInterval(this.timer)
          this.timer = 0
        }
        // eslint-disable-next-line no-magic-numbers
      }, 1000)
    }
  }

  handleOpenAgreement() {
    window.open('https://docs.datastory.com.cn/standalone/docs/1572', '_blank')
  }
}
